import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { CREDIT_PACKAGES } from '@boletra/shared';

interface Props {
  lang: string;
}

type RowKind = 'bad' | 'meh' | 'good';

interface CompareRow {
  label: string;
  traditional: string;
  traditionalKind: RowKind;
  boletra: string;
  boletraKind: RowKind;
}

function useRows(): CompareRow[] {
  const { t } = useTranslation('landing');
  return [
    {
      label: t('comparison.rows.commission_label'),
      traditional: t('comparison.rows.commission_legacy'),
      traditionalKind: 'bad',
      boletra: t('comparison.rows.commission_boletra'),
      boletraKind: 'good',
    },
    {
      label: t('comparison.rows.payout_label'),
      traditional: t('comparison.rows.payout_legacy'),
      traditionalKind: 'bad',
      boletra: t('comparison.rows.payout_boletra'),
      boletraKind: 'good',
    },
    {
      label: t('comparison.rows.money_label'),
      traditional: t('comparison.rows.money_legacy'),
      traditionalKind: 'bad',
      boletra: t('comparison.rows.money_boletra'),
      boletraKind: 'good',
    },
    {
      label: t('comparison.rows.fee_label'),
      traditional: t('comparison.rows.fee_legacy'),
      traditionalKind: 'bad',
      boletra: t('comparison.rows.fee_boletra'),
      boletraKind: 'good',
    },
    {
      label: t('comparison.rows.setup_label'),
      traditional: t('comparison.rows.setup_legacy'),
      traditionalKind: 'bad',
      boletra: t('comparison.rows.setup_boletra'),
      boletraKind: 'good',
    },
    {
      label: t('comparison.rows.contracts_label'),
      traditional: t('comparison.rows.contracts_legacy'),
      traditionalKind: 'bad',
      boletra: t('comparison.rows.contracts_boletra'),
      boletraKind: 'good',
    },
    {
      label: t('comparison.rows.antipiracy_label'),
      traditional: t('comparison.rows.antipiracy_legacy'),
      traditionalKind: 'bad',
      boletra: t('comparison.rows.antipiracy_boletra'),
      boletraKind: 'good',
    },
    {
      label: t('comparison.rows.offline_label'),
      traditional: t('comparison.rows.offline_legacy'),
      traditionalKind: 'meh',
      boletra: t('comparison.rows.offline_boletra'),
      boletraKind: 'good',
    },
    {
      label: t('comparison.rows.multilang_label'),
      traditional: t('comparison.rows.multilang_legacy'),
      traditionalKind: 'meh',
      boletra: t('comparison.rows.multilang_boletra'),
      boletraKind: 'good',
    },
    {
      label: t('comparison.rows.analytics_label'),
      traditional: t('comparison.rows.analytics_legacy'),
      traditionalKind: 'meh',
      boletra: t('comparison.rows.analytics_boletra'),
      boletraKind: 'good',
    },
  ];
}

export function ComparisonSection({ lang }: Props) {
  const { t } = useTranslation('landing');
  const rows = useRows();

  return (
    <section
      id="vs"
      aria-labelledby="vs-heading"
      className="border-t border-neutral-900 py-14 sm:py-20 scroll-mt-20"
    >
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-10 sm:mb-14">
          <p className="text-xs uppercase tracking-widest text-[var(--color-accent)] font-semibold mb-2">
            {t('comparison.eyebrow')}
          </p>
          <h2 id="vs-heading" className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight">
            {t('comparison.title_prefix')}
            <span className="text-[var(--color-accent)]">{t('comparison.title_highlight')}</span>
            {t('comparison.title_suffix')}
          </h2>
          <p className="text-neutral-400 mt-4 text-base sm:text-lg">{t('comparison.subtitle')}</p>
        </div>

        {/* Comparison table */}
        <CompareTable rows={rows} />

        {/* Savings calculator */}
        <SavingsExample className="mt-10 sm:mt-14" />

        {/* Closing CTA */}
        <div className="mt-10 sm:mt-14 text-center">
          <p className="text-neutral-300 text-sm sm:text-base max-w-2xl mx-auto">
            {t('comparison.closing_prefix')}
            <span className="text-[var(--color-accent)] font-semibold">
              {t('comparison.closing_legacy_amount')}
            </span>
            {t('comparison.closing_mid')}
            <br />
            {t('comparison.closing_boletra_prefix')}
            <span className="text-[var(--color-accent)] font-semibold">
              {t('comparison.closing_boletra_amount')}
            </span>
            {t('comparison.closing_suffix')}
          </p>
          <Link
            to={`/${lang}/auth/signup`}
            className="btn-primary mt-6 sm:mt-7 inline-flex"
            aria-label={t('comparison.switch_cta_aria')}
          >
            {t('comparison.switch_cta')}
          </Link>
        </div>
      </div>
    </section>
  );
}

// ---------------------- Compare table ----------------------

function CompareTable({ rows }: { rows: CompareRow[] }) {
  const { t } = useTranslation('landing');
  return (
    <div className="overflow-hidden rounded-2xl border border-neutral-900">
      {/* Header */}
      <div className="hidden md:grid md:grid-cols-[1.1fr_1.4fr_1.4fr] bg-neutral-950/80" role="row">
        <div className="px-5 py-4 text-xs uppercase tracking-widest text-neutral-500 font-semibold">
          {t('comparison.table_category')}
        </div>
        <div className="px-5 py-4 border-l border-neutral-900">
          <p className="text-xs uppercase tracking-widest text-neutral-500 font-semibold">
            {t('comparison.table_legacy_label')}
          </p>
          <p className="text-[11px] text-neutral-600 mt-1">{t('comparison.table_legacy_sub')}</p>
        </div>
        <div className="px-5 py-4 border-l border-[var(--color-accent)]/30 bg-[var(--color-accent)]/[0.04]">
          <p className="text-xs uppercase tracking-widest text-[var(--color-accent)] font-semibold">
            Boletra
          </p>
          <p className="text-[11px] text-neutral-500 mt-1">{t('comparison.table_boletra_sub')}</p>
        </div>
      </div>

      <ul className="divide-y divide-neutral-900">
        {rows.map((row) => (
          <li key={row.label} className="block">
            {/* Mobile layout: stacked */}
            <div className="md:hidden p-5">
              <p className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-3">
                {row.label}
              </p>
              <div className="grid gap-3">
                <CompareCell
                  heading={t('comparison.table_legacy_label')}
                  text={row.traditional}
                  kind={row.traditionalKind}
                  variant="traditional"
                />
                <CompareCell
                  heading="Boletra"
                  text={row.boletra}
                  kind={row.boletraKind}
                  variant="boletra"
                />
              </div>
            </div>

            {/* Desktop layout: 3-col grid */}
            <div className="hidden md:grid md:grid-cols-[1.1fr_1.4fr_1.4fr] items-stretch">
              <div className="px-5 py-5 flex items-center text-sm font-semibold text-neutral-200">
                {row.label}
              </div>
              <div className="px-5 py-5 border-l border-neutral-900 flex items-start gap-3 text-sm text-neutral-400">
                <KindIcon kind={row.traditionalKind} />
                <span>{row.traditional}</span>
              </div>
              <div className="px-5 py-5 border-l border-[var(--color-accent)]/30 bg-[var(--color-accent)]/[0.04] flex items-start gap-3 text-sm text-neutral-100">
                <KindIcon kind={row.boletraKind} />
                <span>{row.boletra}</span>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function CompareCell({
  heading,
  text,
  kind,
  variant,
}: {
  heading: string;
  text: string;
  kind: RowKind;
  variant: 'traditional' | 'boletra';
}) {
  return (
    <div
      className={[
        'rounded-xl border p-4',
        variant === 'boletra'
          ? 'border-[var(--color-accent)]/30 bg-[var(--color-accent)]/[0.06]'
          : 'border-neutral-800 bg-neutral-950/40',
      ].join(' ')}
    >
      <div className="flex items-start gap-3">
        <KindIcon kind={kind} />
        <div className="min-w-0">
          <p
            className={[
              'text-[11px] uppercase tracking-widest font-semibold mb-1',
              variant === 'boletra' ? 'text-[var(--color-accent)]' : 'text-neutral-500',
            ].join(' ')}
          >
            {heading}
          </p>
          <p
            className={[
              'text-sm leading-relaxed',
              variant === 'boletra' ? 'text-neutral-100' : 'text-neutral-300',
            ].join(' ')}
          >
            {text}
          </p>
        </div>
      </div>
    </div>
  );
}

function KindIcon({ kind }: { kind: RowKind }) {
  if (kind === 'good') {
    return (
      <span
        aria-hidden
        className="mt-0.5 inline-flex w-5 h-5 flex-shrink-0 items-center justify-center rounded-full bg-[var(--color-accent)]/15 text-[var(--color-accent)]"
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </span>
    );
  }
  if (kind === 'bad') {
    return (
      <span
        aria-hidden
        className="mt-0.5 inline-flex w-5 h-5 flex-shrink-0 items-center justify-center rounded-full bg-red-500/10 text-red-400"
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <line x1="6" y1="6" x2="18" y2="18" />
          <line x1="18" y1="6" x2="6" y2="18" />
        </svg>
      </span>
    );
  }
  return (
    <span
      aria-hidden
      className="mt-0.5 inline-flex w-5 h-5 flex-shrink-0 items-center justify-center rounded-full bg-amber-500/10 text-amber-400"
    >
      <svg
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <line x1="5" y1="12" x2="19" y2="12" />
      </svg>
    </span>
  );
}

// ---------------------- Savings example ----------------------

interface Preset {
  tickets: number;
  priceUsd: number;
}

const PRESETS: readonly Preset[] = [
  { tickets: 100, priceUsd: 15 },
  { tickets: 500, priceUsd: 20 },
  { tickets: 1500, priceUsd: 35 },
] as const;

/**
 * Picks the per-ticket Boletra cost from the published credit packages,
 * approximating the credit pack a real organizer of that size would buy.
 * Falls back to the largest tier (cheapest per-unit) when the volume exceeds it.
 */
function pickPerTicketUsd(tickets: number): number {
  const sorted = [...CREDIT_PACKAGES].sort((a, b) => a.credits - b.credits);
  const match = sorted.find((p) => p.credits >= tickets) ?? sorted[sorted.length - 1];
  if (!match) return 0;
  return match.perUnitCents / 100;
}

function SavingsExample({ className }: { className?: string }) {
  const { t, i18n } = useTranslation('landing');
  const fmtUsd = useMemo(
    () =>
      new Intl.NumberFormat(i18n.language, {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
      }),
    [i18n.language],
  );
  const fmtNum = useMemo(() => new Intl.NumberFormat(i18n.language), [i18n.language]);

  const [presetIdx, setPresetIdx] = useState(1);
  const preset: Preset = PRESETS[presetIdx] ?? PRESETS[0]!;
  const gross = preset.tickets * preset.priceUsd;
  // Industry-typical traditional fee: ~10% commission on the organizer side.
  const traditionalFee = gross * 0.1;
  const traditionalNet = gross - traditionalFee;
  const boletraPerTicket = pickPerTicketUsd(preset.tickets);
  // First 50 tickets every month are free.
  const billableTickets = Math.max(0, preset.tickets - 50);
  const boletraCost = billableTickets * boletraPerTicket;
  const boletraNet = gross - boletraCost;
  const savings = boletraNet - traditionalNet;

  return (
    <div
      className={[
        'rounded-3xl border border-neutral-900 bg-neutral-950/60 p-6 sm:p-8',
        className ?? '',
      ].join(' ')}
    >
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6 sm:mb-8">
        <div>
          <p className="text-xs uppercase tracking-widest text-[var(--color-accent)] font-semibold mb-2">
            {t('comparison.savings_eyebrow')}
          </p>
          <h3 className="text-xl sm:text-2xl font-bold tracking-tight">
            {t('comparison.savings_title')}
          </h3>
        </div>
        <div
          className="inline-flex rounded-xl border border-neutral-800 bg-neutral-900/60 p-1 self-start sm:self-auto"
          role="tablist"
          aria-label={t('comparison.savings_size_aria')}
        >
          {PRESETS.map((p, i) => {
            const active = i === presetIdx;
            return (
              <button
                key={p.tickets}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setPresetIdx(i)}
                className={[
                  'px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition',
                  active
                    ? 'bg-[var(--color-accent)] text-[var(--color-accent-foreground)]'
                    : 'text-neutral-400 hover:text-neutral-100',
                ].join(' ')}
              >
                {fmtNum.format(p.tickets)}{' '}
                <span className={active ? 'opacity-70' : 'text-neutral-600'}>
                  × {fmtUsd.format(p.priceUsd)}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <SavingsCard
          variant="traditional"
          title={t('comparison.savings_legacy_title')}
          tag={t('comparison.savings_legacy_tag')}
          gross={gross}
          fee={traditionalFee}
          feeLabel={t('comparison.savings_legacy_fee_label')}
          net={traditionalNet}
          netLabel={t('comparison.savings_net_label')}
          grossLabel={t('comparison.savings_gross_label')}
          payoutLine={t('comparison.savings_legacy_payout')}
          fmtUsd={fmtUsd}
        />
        <SavingsCard
          variant="boletra"
          title="Boletra"
          tag={t('comparison.savings_boletra_tag', { amount: boletraPerTicket })}
          gross={gross}
          fee={boletraCost}
          feeLabel={t('comparison.savings_boletra_fee_label', { count: billableTickets })}
          net={boletraNet}
          netLabel={t('comparison.savings_net_label')}
          grossLabel={t('comparison.savings_gross_label')}
          payoutLine={t('comparison.savings_boletra_payout')}
          fmtUsd={fmtUsd}
        />
      </div>

      <div className="mt-6 sm:mt-7 rounded-2xl border border-[var(--color-accent)]/30 bg-[var(--color-accent)]/10 px-5 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <p className="text-sm text-neutral-300">{t('comparison.savings_diff_label')}</p>
        <p className="text-2xl sm:text-3xl font-bold text-[var(--color-accent)] leading-none">
          + {fmtUsd.format(savings)}
        </p>
      </div>

      <p className="text-[11px] text-neutral-500 mt-4">{t('comparison.savings_footer')}</p>
    </div>
  );
}

function SavingsCard({
  variant,
  title,
  tag,
  gross,
  fee,
  feeLabel,
  net,
  netLabel,
  grossLabel,
  payoutLine,
  fmtUsd,
}: {
  variant: 'traditional' | 'boletra';
  title: string;
  tag: string;
  gross: number;
  fee: number;
  feeLabel: string;
  net: number;
  netLabel: string;
  grossLabel: string;
  payoutLine: string;
  fmtUsd: Intl.NumberFormat;
}) {
  const isBoletra = variant === 'boletra';
  return (
    <div
      className={[
        'relative rounded-2xl p-5 sm:p-6 border flex flex-col',
        isBoletra
          ? 'border-[var(--color-accent)]/40 bg-gradient-to-br from-[var(--color-accent)]/10 to-neutral-950'
          : 'border-neutral-800 bg-neutral-900/40',
      ].join(' ')}
    >
      <div className="flex items-center justify-between gap-3 mb-4">
        <h4 className="font-semibold text-base sm:text-lg">{title}</h4>
        <span
          className={[
            'text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 rounded-full whitespace-nowrap',
            isBoletra
              ? 'bg-[var(--color-accent)] text-[var(--color-accent-foreground)]'
              : 'bg-neutral-800 text-neutral-300',
          ].join(' ')}
        >
          {tag}
        </span>
      </div>

      <dl className="space-y-3 text-sm">
        <div className="flex items-baseline justify-between text-neutral-400">
          <dt>{grossLabel}</dt>
          <dd className="tabular-nums">{fmtUsd.format(gross)}</dd>
        </div>
        <div className="flex items-baseline justify-between text-neutral-400 gap-3">
          <dt className="truncate">{feeLabel}</dt>
          <dd className="tabular-nums text-red-400 whitespace-nowrap">- {fmtUsd.format(fee)}</dd>
        </div>
        <div className="border-t border-neutral-800 pt-3 flex items-baseline justify-between">
          <dt className="text-neutral-300 font-medium">{netLabel}</dt>
          <dd
            className={[
              'tabular-nums text-2xl sm:text-3xl font-bold',
              isBoletra ? 'text-[var(--color-accent)]' : 'text-neutral-200',
            ].join(' ')}
          >
            {fmtUsd.format(net)}
          </dd>
        </div>
      </dl>

      <p className="text-xs text-neutral-500 mt-4 leading-relaxed">{payoutLine}</p>
    </div>
  );
}
